import type { WorkflowEnumItem } from "../hooks/useWorkflowEnums";

export const CRM_ACTION_CODE_PREFIX = "Action.Crm.";

const LOG_IN_CRM_ACTION_CODE = `${CRM_ACTION_CODE_PREFIX}LogInCrm`;

export type LogInCrmActionNodeSummaryMode = "simple" | "extended";

export function resolveLogInCrmActionNodeSummaryMode(controller: unknown): LogInCrmActionNodeSummaryMode {
    const raw = workflowControllerFields(controller).getWorkflowActionMetaConfigs;
    if (!isRecord(raw)) return "simple";
    const entry = raw[LOG_IN_CRM_ACTION_CODE];
    if (!isRecord(entry)) return "simple";
    const meta = entry.meta;
    if (!isRecord(meta)) return "simple";

    const explicitMode = meta.logInCrmNodeSummaryMode;
    if (explicitMode === "simple" || explicitMode === "extended") {
        return explicitMode;
    }

    const formFields = meta.formFields;
    if (isParentStyleSingleCheckboxLogInCrmForm(formFields)) return "simple";
    return "extended";
}

function isParentStyleSingleCheckboxLogInCrmForm(formFields: unknown): boolean {
    if (!Array.isArray(formFields) || formFields.length !== 1) return false;
    const field = formFields[0];
    if (!isRecord(field)) return false;
    return field.key === "loggingPreference" && field.type === "checkbox";
}

export function isCrmActionCode(code: string | undefined): boolean {
    return Boolean(code?.startsWith(CRM_ACTION_CODE_PREFIX));
}

export type WorkflowCrmActionHelpOverride = { title?: string; body?: string };

export type WorkflowCrmActionEnumPatch = Partial<
    Pick<WorkflowEnumItem, "idleLabel" | "activeLabel" | "order" | "meta">
>;

type HelpPanelCopy = { title: string; body: string };

type ControllerWorkflowFields = {
    getWorkflowActionMetaConfigs?: unknown;
};

function workflowControllerFields(controller: unknown): ControllerWorkflowFields {
    if (controller !== null && typeof controller === "object" && !Array.isArray(controller)) {
        return controller as ControllerWorkflowFields;
    }
    return {};
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function trimmedNonEmpty(value: unknown): string | undefined {
    if (typeof value !== "string") return undefined;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
}

function parseHelpOverride(value: unknown): WorkflowCrmActionHelpOverride | null {
    if (!isRecord(value)) return null;
    const title = trimmedNonEmpty(value.title);
    const body = trimmedNonEmpty(value.body);
    if (!title && !body) return null;
    return { ...(title !== undefined && { title }), ...(body !== undefined && { body }) };
}

function helpOverridesFromActionMetaConfigs(raw: unknown): Record<string, WorkflowCrmActionHelpOverride> | undefined {
    if (!isRecord(raw)) return undefined;
    const byActionCode: Record<string, WorkflowCrmActionHelpOverride> = {};
    for (const [actionCode, patch] of Object.entries(raw)) {
        if (!actionCode.startsWith(CRM_ACTION_CODE_PREFIX) || !isRecord(patch)) continue;
        const parsed = parseHelpOverride(patch.helpContent);
        if (parsed) byActionCode[actionCode] = parsed;
    }
    return Object.keys(byActionCode).length > 0 ? byActionCode : undefined;
}

export function readWorkflowCrmHelpOverrides(
    controller: unknown
): Record<string, WorkflowCrmActionHelpOverride> | undefined {
    return helpOverridesFromActionMetaConfigs(workflowControllerFields(controller).getWorkflowActionMetaConfigs);
}

function buildActionEnumPatch(raw: Record<string, unknown>): WorkflowCrmActionEnumPatch | null {
    const patch: WorkflowCrmActionEnumPatch = {};
    if (typeof raw.idleLabel === "string") patch.idleLabel = raw.idleLabel;
    if (typeof raw.activeLabel === "string") patch.activeLabel = raw.activeLabel;
    if (typeof raw.order === "number" && Number.isFinite(raw.order)) patch.order = raw.order;
    if (raw.meta !== undefined && isRecord(raw.meta)) {
        patch.meta = raw.meta as WorkflowEnumItem["meta"];
    }
    return Object.keys(patch).length > 0 ? patch : null;
}

export function readWorkflowActionMetaConfigs(
    controller: unknown
): Record<string, WorkflowCrmActionEnumPatch> | undefined {
    const raw = workflowControllerFields(controller).getWorkflowActionMetaConfigs;
    if (!isRecord(raw)) return undefined;
    const patchesByCode: Record<string, WorkflowCrmActionEnumPatch> = {};
    for (const [actionCode, patchValue] of Object.entries(raw)) {
        if (!actionCode.startsWith(CRM_ACTION_CODE_PREFIX) || !isRecord(patchValue)) continue;
        const patch = buildActionEnumPatch(patchValue);
        if (patch) patchesByCode[actionCode] = patch;
    }
    return Object.keys(patchesByCode).length > 0 ? patchesByCode : undefined;
}

export function mergeCrmActionEnumPatches(
    actionTypes: WorkflowEnumItem[],
    patches: Record<string, WorkflowCrmActionEnumPatch> | undefined
): WorkflowEnumItem[] {
    if (!patches || Object.keys(patches).length === 0) return actionTypes;
    return actionTypes.map((item) => {
        if (!item.code.startsWith(CRM_ACTION_CODE_PREFIX)) return item;
        const patch = patches[item.code];
        if (!patch) return item;
        const mergedItem: WorkflowEnumItem = { ...item };
        if (patch.idleLabel !== undefined) mergedItem.idleLabel = patch.idleLabel;
        if (patch.activeLabel !== undefined) mergedItem.activeLabel = patch.activeLabel;
        if (patch.order !== undefined) mergedItem.order = patch.order;
        if (patch.meta !== undefined) {
            mergedItem.meta = { ...item.meta, ...patch.meta };
        }
        return mergedItem;
    });
}

export function resolveCrmActionHelpContent(
    actionCode: string | undefined,
    formType: string,
    overrides: Record<string, WorkflowCrmActionHelpOverride> | undefined,
    staticCopyByFormType: Record<string, HelpPanelCopy>
): HelpPanelCopy {
    const fallbackCopy = staticCopyByFormType[formType] ?? staticCopyByFormType.default;
    if (!isCrmActionCode(actionCode)) return fallbackCopy;

    const crmOverride = actionCode ? overrides?.[actionCode] : undefined;
    if (!crmOverride) return fallbackCopy;

    return {
        title: crmOverride.title ?? fallbackCopy.title,
        body: crmOverride.body ?? fallbackCopy.body,
    };
}
